import { Box, Button, Flex, Header, spaces, Tooltip } from '@electricjs/arc';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { FormActionsContainer, TooltipReference } from './common';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { OnboardingFormData } from './types';
import OnboardEmployeeModalApps from './OnboardEmployeeModalApps';
import { SkipApplicationSetupModal } from './SkipApplicationSetupModal';
import AddLicensesModal from '@/components/Licenses/AddLicensesModal';
import EmployeeGroupSelector from '../People/EmployeeProfile/EmployeeGroupSelectorField';
import ReadOnlyGroupChips from '../People/EmployeeProfile/ReadOnlyGroupChips';
import {
  useManageableGroups,
  useReadOnlyGroups,
} from '../People/EmployeeProfile/groupSelectorHelpers';
import { useGetOrganizationGroupsQuery } from '@/redux/slices/organizationApiSlice';
import { useLazyGetOrganizationGroupsForEmployeeQuery } from '@/redux/slices/employeeApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Group, GroupMembershipType } from '@/types/groups';
import { SelectionParamKeys } from '@/types/queryParamKeys';
import { RequestTypeCategoryNameEnum } from '@/hooks/useGetRequestTypeCategoryChampionEmployee';
import useIsRequestTypeCategoryChampion from './hooks/useIsRequestTypeCategoryChampion';
import { CenteredSpinner } from '@common';
import { useTranslation } from 'react-i18next';

const GroupsContainer = styled(Flex)`
  width: 100%;
  margin-top: 2.6rem;
`;

type ApplicationsProps = {
  addToGroups: (employeeGroups: Group[], onSuccess: () => void) => void;
  employeeId: string;
  isAddingEmployee: boolean;
  submitOnboarding: (
    employeeId: string,
    onSuccess: () => void,
    needsHardware?: boolean,
    needsApplications?: boolean,
    watchers?: string[]
  ) => void;
  isHRISActiveEmployeeOnboarding?: boolean;
  hasAvailableLicenses: boolean;
  onLicenseSessionReset: () => void;
};

const Applications = ({
  addToGroups,
  employeeId,
  isAddingEmployee,
  submitOnboarding,
  isHRISActiveEmployeeOnboarding = false,
  hasAvailableLicenses,
  onLicenseSessionReset,
}: ApplicationsProps) => {
  const { t } = useTranslation('common');
  const { ithOnboardingAppsTable, selfServiceSeatAmendment } = useFlags();
  const navigate = useNavigate();
  const [queryParams, writeQueryParams] = useSearchParams();
  const { control, reset, setValue, watch, getValues } =
    useFormContext<OnboardingFormData>();
  const [
    employeeGroups,
    needsHardware,
    watchers,
    additionalApps,
    excludedApps,
  ] = useWatch({
    control,
    name: [
      'groups',
      'needsHardware',
      'watchers',
      'additionalApps',
      'excludedApps',
    ],
  });

  const organizationId = useGetOrganizationId();
  const { data: groupsData = [], isLoading: isLoadingGroups } =
    useGetOrganizationGroupsQuery({ organizationId });
  const [getGroupsForEmployee] = useLazyGetOrganizationGroupsForEmployeeQuery();

  // Force-refresh the employee's groups on mount so any dynamic groups they were
  // just auto-assigned to (e.g. via HRIS-driven rules) are reflected in the form,
  // then merge those dynamic groups into `groups` while preserving static selections.
  useEffect(() => {
    if (!employeeId) return;
    getGroupsForEmployee({ organizationId, employeeId }).then(({ data }) => {
      if (!data) return;
      const freshDynamicGroups = data.filter(
        g => g.membershipType === GroupMembershipType.Dynamic
      );
      const currentStaticGroups = (getValues('groups') ?? []).filter(
        g => g.membershipType !== GroupMembershipType.Dynamic
      );
      setValue('groups', [...currentStaticGroups, ...freshDynamicGroups], {
        shouldDirty: true,
      });
    });
  }, [employeeId, organizationId, getGroupsForEmployee, getValues, setValue]);
  const [isLoadingApps, setIsLoadingApps] = useState(true);
  const isCurrentUserHardwareChampion = useIsRequestTypeCategoryChampion(
    RequestTypeCategoryNameEnum.DEVICE
  );
  const { openModal, closeModal } = useGlobalUI();

  // Add the employee to the groups selected, create the onboarding and restart the onboarding form
  const handleFinishAndRestart = () => {
    // Remove the existingEmployeeId param from the URL before starting a new onboarding
    queryParams.delete('existingEmployeeId');
    writeQueryParams(queryParams, {
      // Each change in the query params will replace the current history state, meaning the "back button" will go back to the previous page.
      replace: true,
    });
    const doSubmit = () =>
      submitOnboarding(
        employeeId,
        () => {
          reset();
          onLicenseSessionReset();
        },
        false,
        true,
        watchers
      );
    if ((employeeGroups ?? []).length > 0) {
      addToGroups(employeeGroups ?? [], doSubmit);
    } else {
      doSubmit();
    }
  };

  // When the org is out of licenses and can self-serve, offer to buy more before restarting.
  // Otherwise, just restart — the no-licenses banner will show on the new onboarding as normal.
  const handleFinishAndRestartClick = () => {
    if (!hasAvailableLicenses && selfServiceSeatAmendment) {
      openModal(
        <AddLicensesModal
          onClose={closeModal}
          onSuccess={() => {
            closeModal();
            handleFinishAndRestart();
          }}
        />
      );
      return;
    }
    handleFinishAndRestart();
  };

  // Add the employee to the selected groups.
  // Create the onboarding process and navigate to the next page.
  //  - If hardware is needed, go to the hardware procurement page.
  //  - Otherwise, go to the employee's profile page.
  const handleNext = () => {
    const doSubmit = () =>
      submitOnboarding(
        employeeId,
        () => {
          const newUrl =
            needsHardware && isCurrentUserHardwareChampion
              ? `/get-hardware?${SelectionParamKeys.EmployeeId}=${employeeId}`
              : `/employees/${employeeId}`;
          navigate(newUrl);
        },
        needsHardware,
        true,
        watchers
      );
    if ((employeeGroups ?? []).length > 0) {
      addToGroups(employeeGroups ?? [], doSubmit);
    } else {
      doSubmit();
    }
  };

  /**
   * Completes the flow when the user skips application setup
   * (via the Skip Application Setup modal).
   *
   * Adds the employee to the selected groups, then submits without
   * creating onboarding requests, and navigates to the employee profile.
   *
   * Used as onComplete for SkipApplicationSetupModal.
   * Unlike handleNext, this path passes needsApplications: false to
   * submitOnboarding so that no onboarding requests are created.
   *
   * This is done either because the employee already has access to its
   * needed applications or because the admin wants to set up application
   * access manually later.
   *
   * In both cases, the onboarding process is completed immediately
   * after adding to groups, and the user is taken to the employee profile page.
   *
   * The main usage of this path is for HRIS-ingested employees that were
   * activated while getting hardware orders. As of now that is only
   * happening for Trinet embeddings, but it could be used for
   * other HRIS integrations in the future.
   */
  const handleSkipOnboarding = () => {
    const doSubmit = () =>
      submitOnboarding(
        employeeId,
        () => navigate(`/employees/${employeeId}`),
        false,
        false,
        watchers
      );
    if ((employeeGroups ?? []).length > 0) {
      addToGroups(employeeGroups ?? [], doSubmit);
    } else {
      doSubmit();
    }
  };

  const orgHasNoGroups = !groupsData.length;
  const hasNoAppsOrGroups = ithOnboardingAppsTable
    ? (employeeGroups ?? []).length === 0 && (additionalApps ?? []).length === 0
    : orgHasNoGroups;
  const nextButtonLabel =
    needsHardware && isCurrentUserHardwareChampion
      ? t('onboardings.applications.nextButton')
      : t('onboardings.applications.finishButton');

  const manageableGroups = useManageableGroups(groupsData);
  const readOnlyGroups = useReadOnlyGroups(employeeGroups ?? []);

  return (
    <>
      <Header
        title={t('onboardings.applications.title')}
        subtitle={t('onboardings.applications.subtitle')}
      />
      <Box width="100%">
        <EmployeeGroupSelector
          setValue={setValue}
          watch={watch}
          label={t('onboardings.applications.employeeGroupsLabel')}
          required={!ithOnboardingAppsTable}
          groups={manageableGroups}
          disabled={isLoadingGroups}
          placeholder={
            groupsData?.length > 0
              ? t('onboardings.applications.assignGroupsPlaceholder')
              : t('onboardings.applications.noGroupsPlaceholder')
          }
          lockedGroups={readOnlyGroups}
        />
        <ReadOnlyGroupChips groups={readOnlyGroups} />
      </Box>
      <Box mt={spaces.space_s_rem}>
        <Tooltip
          pr="1rem"
          maxWidth="58rem"
          content={t('onboardings.applications.tooltipContent')}>
          <TooltipReference>
            {t('onboardings.applications.tooltipReference')}
          </TooltipReference>
        </Tooltip>
      </Box>
      {isLoadingGroups ? (
        <CenteredSpinner py="4.3rem" />
      ) : (
        <GroupsContainer>
          <OnboardEmployeeModalApps
            orgHasNoGroups={orgHasNoGroups}
            employeeGroups={employeeGroups ?? []}
            onLoadingChange={setIsLoadingApps}
          />
        </GroupsContainer>
      )}
      <FormActionsContainer>
        <Flex width="100%" gap="2rem" justifyContent="flex-end">
          {isHRISActiveEmployeeOnboarding ? (
            <Button
              id="skip-application-setup-button"
              variant="text"
              onClick={() =>
                openModal(
                  <SkipApplicationSetupModal
                    employeeId={employeeId}
                    employeeGroups={employeeGroups ?? []}
                    excludedAppIds={(excludedApps ?? []).map(a => a.id)}
                    onClose={closeModal}
                    onComplete={handleSkipOnboarding}
                  />
                )
              }
              disabled={
                isLoadingGroups ||
                isLoadingApps ||
                isAddingEmployee ||
                hasNoAppsOrGroups
              }>
              {t('onboardings.applications.skipApplicationSetupButton')}
            </Button>
          ) : (
            <Button
              id="finish-and-restart-button"
              variant="text"
              onClick={handleFinishAndRestartClick}
              disabled={
                isLoadingGroups ||
                isLoadingApps ||
                isAddingEmployee ||
                hasNoAppsOrGroups
              }>
              {t('onboardings.applications.finishAndRestartButton')}
            </Button>
          )}
          <Button
            id="next-button"
            onClick={handleNext}
            disabled={
              isLoadingGroups ||
              isLoadingApps ||
              isAddingEmployee ||
              hasNoAppsOrGroups
            }
            loading={isAddingEmployee}>
            {nextButtonLabel}
          </Button>
        </Flex>
      </FormActionsContainer>
    </>
  );
};

export default Applications;
